import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

/**
 * StockInventory — shows vehicles scraped from the dealer website.
 * Tabs: Stock List | Sync Status
 */

const STATE_COLOURS = {
  success: '#33b86c',
  failed: '#ef5350',
  pending: '#ffd740',
};

const FUEL_BADGE = {
  Petrol: { bg: 'rgba(49,126,235,0.18)', color: '#6eaaff' },
  Diesel: { bg: 'rgba(255,215,64,0.18)', color: '#ffd740' },
  Electric: { bg: 'rgba(51,184,108,0.18)', color: '#33b86c' },
  Hybrid: { bg: 'rgba(72,20,100,0.25)', color: '#b28fff' },
};

function Badge({ children, colour }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.04em',
      background: colour?.bg || 'rgba(44,53,78,0.6)',
      color: colour?.color || '#8a93a9',
    }}>
      {children}
    </span>
  );
}

function relativeTime(ts) {
  if (!ts) return 'Never';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function StockInventory() {
  const [activeTab, setActiveTab] = useState('stock');
  const [stock, setStock] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const loadStock = useCallback(async () => {
    setLoading(true);
    try {
      const [stockData, statusData] = await Promise.all([
        api.request('/api/admin/stock-scrape/stock', { method: 'GET' }),
        api.request('/api/admin/stock-scrape/status', { method: 'GET' }),
      ]);
      setStock(stockData || []);
      setStatus(statusData || null);
    } catch (e) {
      showToast('error', 'Failed to load stock data');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadStock(); }, [loadStock]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.request('/api/admin/stock-scrape/trigger', { method: 'POST' });
      showToast('success', 'Stock sync started — this takes ~2 minutes. Refresh when done.');
    } catch (e) {
      showToast('error', 'Failed to trigger sync');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = stock.filter(v => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (v.make || '').toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q) ||
      (v.title || '').toLowerCase().includes(q) ||
      (v.body_type || '').toLowerCase().includes(q) ||
      (v.fuel || '').toLowerCase().includes(q) ||
      String(v.year || '').includes(q)
    );
  });

  const tabs = [
    { id: 'stock', label: 'Live Stock' },
    { id: 'status', label: 'Sync Status' },
  ];

  return (
    <div style={s.wrap}>
      {/* Toast */}
      {toast && (
        <div style={{ ...s.toast, background: toast.type === 'error' ? 'rgba(239,83,80,0.18)' : 'rgba(51,184,108,0.18)', borderColor: toast.type === 'error' ? '#ef5350' : '#33b86c' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.title}>Website Stock</div>
          <div style={s.subtitle}>
            {status?.stock_count ?? '—'} vehicles live &nbsp;·&nbsp; {status?.kb_entry_count ?? '—'} KB entries &nbsp;·&nbsp; Last sync: {relativeTime(status?.config?.last_sync_at)}
          </div>
        </div>
        <button
          style={{ ...s.btn, ...(syncing ? s.btnDisabled : {}) }}
          onClick={triggerSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {tabs.map(t => (
          <button
            key={t.id}
            style={{ ...s.tab, ...(activeTab === t.id ? s.tabActive : {}) }}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── STOCK TAB ── */}
      {activeTab === 'stock' && (
        <>
          <input
            style={s.search}
            placeholder="Search make, model, body type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          {loading ? (
            <div style={s.empty}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={s.empty}>
              {stock.length === 0
                ? 'No stock scraped yet. Hit Sync Now to pull vehicles from the website.'
                : 'No vehicles match your search.'}
            </div>
          ) : (
            <div style={s.grid}>
              {filtered.map(v => (
                <div
                  key={v.listing_id}
                  style={s.card}
                  onClick={() => setSelectedVehicle(selectedVehicle?.listing_id === v.listing_id ? null : v)}
                >
                  {/* Price badge */}
                  <div style={s.cardPrice}>
                    {v.price ? `£${v.price.toLocaleString()}` : 'POA'}
                  </div>

                  {/* Vehicle title */}
                  <div style={s.cardTitle}>
                    {v.year} {v.make} {v.model}
                  </div>
                  <div style={s.cardTrim}>
                    {v.title?.replace(`${v.year} ${v.make} ${v.model}`, '').trim()}
                  </div>

                  {/* Key stats */}
                  <div style={s.cardStats}>
                    <span style={s.stat}>{v.mileage ? v.mileage.toLocaleString() + ' mi' : '—'}</span>
                    <span style={s.statDot}>·</span>
                    <span style={s.stat}>{v.year || '—'}</span>
                    <span style={s.statDot}>·</span>
                    <span style={s.stat}>{v.body_type || '—'}</span>
                  </div>

                  {/* Badges */}
                  <div style={s.badges}>
                    <Badge colour={FUEL_BADGE[v.fuel]}>{v.fuel || 'Unknown'}</Badge>
                    {v.transmission && <Badge>{v.transmission}</Badge>}
                    {v.colour && <Badge>{v.colour}</Badge>}
                  </div>

                  {/* Expand detail */}
                  {selectedVehicle?.listing_id === v.listing_id && (
                    <div style={s.detail}>
                      <div style={s.detailRow}>
                        <a href={v.url} target="_blank" rel="noopener noreferrer" style={s.link}>
                          View on website
                        </a>
                        <span style={s.detailMeta}>First seen: {relativeTime(v.first_seen_at)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── STATUS TAB ── */}
      {activeTab === 'status' && (
        <div style={s.statusWrap}>
          {!status?.config ? (
            <div style={s.empty}>No scrape config found.</div>
          ) : (
            <>
              <div style={s.statusCard}>
                <div style={s.statusLabel}>Stock URL</div>
                <div style={s.statusValue}>
                  <a href={status.config.stock_url} target="_blank" rel="noopener noreferrer" style={s.link}>
                    {status.config.stock_url}
                  </a>
                </div>
              </div>

              <div style={s.statusCard}>
                <div style={s.statusLabel}>Last Sync</div>
                <div style={s.statusValue}>
                  {status.config.last_sync_at
                    ? new Date(status.config.last_sync_at).toLocaleString('en-GB')
                    : 'Never'
                  }
                </div>
              </div>

              <div style={s.statusCard}>
                <div style={s.statusLabel}>Last Result</div>
                <div style={{ ...s.statusValue, color: STATUS_COLOUR[status.config.last_sync_status] || '#8a93a9' }}>
                  {status.config.last_sync_status
                    ? status.config.last_sync_status.toUpperCase()
                    : 'PENDING'
                  }
                  {status.config.last_sync_count != null && (
                    <span style={{ color: '#8a93a9', marginLeft: 8, fontSize: 13 }}>
                      ({status.config.last_sync_count} vehicles)
                    </span>
                  )}
                </div>
              </div>

              {status.config.last_sync_error && (
                <div style={{ ...s.statusCard, borderColor: 'rgba(239,83,80,0.4)' }}>
                  <div style={s.statusLabel}>Last Error</div>
                  <div style={{ ...s.statusValue, color: '#ef9a9a', fontSize: 12 }}>
                    {status.config.last_sync_error}
                  </div>
                </div>
              )}

              <div style={s.statusCard}>
                <div style={s.statusLabel}>KB Entries</div>
                <div style={s.statusValue}>{status.kb_entry_count} inventory entries in Knowledge Base</div>
              </div>

              <div style={s.statusCard}>
                <div style={s.statusLabel}>Schedule</div>
                <div style={s.statusValue}>Daily at 5:00 AM (London time)</div>
              </div>

              <div style={s.statusCard}>
                <div style={s.statusLabel}>Enabled</div>
                <div style={{ ...s.statusValue, color: status.config.enabled ? '#33b86c' : '#ef5350' }}>
                  {status.config.enabled ? 'YES' : 'NO'}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_COLOUR = { success: '#33b86c', failed: '#ef5350' };

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 16 },
  toast: {
    padding: '10px 16px', borderRadius: 8, border: '1px solid',
    fontSize: 13, color: '#c6cad6',
  },
  header: {
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
  },
  title: { fontSize: 16, fontWeight: 700, color: '#c6cad6' },
  subtitle: { fontSize: 12, color: '#4a5470', marginTop: 4 },
  btn: {
    padding: '8px 18px', borderRadius: 8, border: 'none',
    background: 'var(--brand-primary, #317eeb)', color: '#fff', fontWeight: 600,
    fontSize: 13, cursor: 'pointer',
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  tabs: { display: 'flex', gap: 4, borderBottom: '1px solid #2c354e', paddingBottom: 0 },
  tab: {
    padding: '8px 16px', background: 'transparent', border: 'none',
    borderBottom: '2px solid transparent', color: '#4a5470',
    fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  tabActive: { color: 'var(--brand-primary, #317eeb)', borderBottom: '2px solid var(--brand-primary, #317eeb)' },
  search: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #2c354e',
    background: '#212942', color: '#c6cad6', fontSize: 13, width: '100%',
  },
  empty: { color: '#4a5470', fontSize: 13, padding: '32px 0', textAlign: 'center' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 12,
  },
  card: {
    background: 'rgba(33,41,66,0.82)', border: '1px solid #2c354e',
    borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
    transition: 'border-color 0.15s',
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  cardPrice: { fontSize: 18, fontWeight: 700, color: '#c6cad6' },
  cardTitle: { fontSize: 14, fontWeight: 600, color: '#c6cad6' },
  cardTrim: { fontSize: 11, color: '#4a5470', marginTop: -4 },
  cardStats: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8a93a9', marginTop: 2 },
  stat: {},
  statDot: { color: '#2c354e' },
  badges: { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 },
  detail: {
    borderTop: '1px solid #2c354e', paddingTop: 10, marginTop: 4,
  },
  detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  detailMeta: { fontSize: 11, color: '#4a5470' },
  link: { color: 'var(--brand-primary, #317eeb)', fontSize: 12, textDecoration: 'none' },
  statusWrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  statusCard: {
    background: 'rgba(33,41,66,0.82)', border: '1px solid #2c354e',
    borderRadius: 10, padding: '12px 16px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: 8,
  },
  statusLabel: { fontSize: 11, color: '#4a5470', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 },
  statusValue: { fontSize: 13, color: '#c6cad6', fontWeight: 500 },
};
